import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/apiClient";
import StageBadge from "@/components/StageBadge";
import ActivityFeed from "@/components/dashboard/ActivityFeed";
import OrderMetaChips from "@/components/order/OrderMetaChips";
import RefreshControl from "@/components/RefreshControl";
import useAutoRefresh from "@/lib/useAutoRefresh";
import useBase44Sync from "@/lib/useBase44Sync";
import { orderProgressPct, isOrderCompleted, isOrderCancelled } from "@/lib/stages";
import { fetchOrdersWithItems } from "@/lib/fetchOrdersWithItems";
import { Card } from "@/components/ui/card";
import {
  Package, CheckCircle, Barcode, ChartLine, ArrowRight, Cube, PaintBrush,
} from "@phosphor-icons/react";

function Kpi({ label, value, accent, icon: Icon }) {
  return (
    <Card className="p-5 border-zinc-200 bg-white" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">{label}</div>
          <div className="mt-2 text-3xl font-heading font-black tabular-nums">{value}</div>
        </div>
        <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: `${accent}22`, color: accent }}>
          <Icon size={22} weight="bold" />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [s, enriched] = await Promise.all([
        api.get("/stats"),
        fetchOrdersWithItems(),
      ]);
      setStats(s.data);
      setOrders(enriched);
    } catch (err) {
      console.error("dashboard load failed", err);
    } finally { setLoading(false); }
  }, []);

  const { refresh, refreshing, lastUpdatedAt, livePolling, setLivePolling } = useAutoRefresh(fetchAll);
  const { runSync, syncing, canSync } = useBase44Sync();

  const activeOrders = orders.filter((o) => !isOrderCompleted(o) && !isOrderCancelled(o));
  const completedOrders = orders.filter((o) => isOrderCompleted(o));

  // Orders with at least one specialty item still pending vendor receipt.
  const awaitingVendor = orders
    .filter((o) => o.has_specialty_items && (o.specialty_received_count || 0) < (o.specialty_count || 0))
    .sort((a, b) => {
      // Sort by delivery_date asc (earliest first), nulls last
      const da = a.delivery_date ? new Date(a.delivery_date).getTime() : Infinity;
      const db = b.delivery_date ? new Date(b.delivery_date).getTime() : Infinity;
      return da - db;
    });
  const totalPendingSpecialty = awaitingVendor.reduce(
    (sum, o) => sum + Math.max(0, (o.specialty_count || 0) - (o.specialty_received_count || 0)),
    0,
  );

  const paintOrders = orders.filter((o) => !isOrderCancelled(o) && Number(o.paint_out_count) > 0);
  const paintItems = paintOrders.reduce((sum, o) => sum + (Number(o.paint_out_count) || 0), 0);
  const paintOldest = paintOrders.reduce((max, o) => Math.max(max, Number(o.paint_oldest_days_out) || 0), 0);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-semibold">Today</div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl mt-1">Operations Overview</h1>
        </div>
        <RefreshControl
          onRefresh={refresh}
          refreshing={refreshing}
          lastUpdatedAt={lastUpdatedAt}
          livePolling={livePolling}
          onToggleLive={setLivePolling}
          onSync={canSync ? () => runSync(refresh) : null}
          syncing={syncing}
          testId="dashboard-refresh"
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
        <Kpi label="Total Orders" value={stats?.total_orders ?? "—"} accent="#C8102E" icon={Package} />
        <Kpi label="Total Items" value={stats?.total_items ?? "—"} accent="#78350F" icon={ChartLine} />
        <Kpi label="Total Scans" value={stats?.total_scans ?? "—"} accent="#3B82F6" icon={Barcode} />
        <Kpi label="My Scans" value={stats?.my_scans ?? "—"} accent="#10B981" icon={CheckCircle} />
      </div>

      {awaitingVendor.length > 0 && (
        <AwaitingVendorReceipt
          orders={awaitingVendor}
          totalPendingSpecialty={totalPendingSpecialty}
        />
      )}

      {paintItems > 0 && (
        <Link
          to="/paint"
          data-testid="dashboard-paint-callout"
          className="block rounded-lg border border-purple-200 bg-purple-50 hover:bg-purple-100 dark:bg-purple-950/30 dark:border-purple-900 dark:hover:bg-purple-950/50 transition-colors px-5 py-4"
        >
          <div className="flex items-center gap-4 flex-wrap">
            <div className="w-11 h-11 rounded-md bg-purple-600/15 text-purple-600 dark:text-purple-300 flex items-center justify-center shrink-0">
              <PaintBrush size={22} weight="bold" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.25em] text-purple-700 dark:text-purple-300 font-bold">At the painter</div>
              <div className="mt-0.5 font-heading font-black text-lg text-purple-900 dark:text-purple-100">
                {paintItems} item{paintItems === 1 ? "" : "s"} across {paintOrders.length} order{paintOrders.length === 1 ? "" : "s"}
                {paintOldest > 0 && <span className="text-sm font-semibold ml-2 text-purple-700 dark:text-purple-300">· oldest {paintOldest}d</span>}
              </div>
            </div>
            <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-purple-700 dark:text-purple-300 inline-flex items-center gap-1">
              View paint <ArrowRight size={12} weight="bold" />
            </span>
          </div>
        </Link>
      )}

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-bold text-xl">Active orders</h2>
          <Link to="/orders" className="text-sm font-semibold text-[#C8102E] hover:underline inline-flex items-center gap-1" data-testid="dashboard-view-all-orders">
            View all <ArrowRight size={14} weight="bold" />
          </Link>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
          <div className="xl:col-span-2 space-y-4">
            {loading ? (
              <Card className="p-8 text-center text-zinc-500">Loading…</Card>
            ) : activeOrders.length === 0 ? (
              <Card className="p-8 text-center text-zinc-500" data-testid="dashboard-no-orders">
                No active orders right now.
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {activeOrders.slice(0, 6).map((o) => {
                  const progress = orderProgressPct(o);
                  return (
                    <Link key={o.id} to={`/orders/${o.id}`} data-testid={`dashboard-order-${o.order_number}`}>
                      <Card className="p-5 border-zinc-200 hover:border-[#C8102E] transition-colors">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Order</div>
                            <div className="font-heading font-black text-lg truncate">{o.order_number}</div>
                            <div className="text-sm text-zinc-600 dark:text-zinc-300 truncate">{o.customer_name}</div>
                            {o.customer_address && (
                              <div className="text-xs text-zinc-500 truncate mt-0.5">{o.customer_address}</div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Items</div>
                            <div className="font-heading font-black text-lg tabular-nums">{o.completed_count}/{o.item_count}</div>
                          </div>
                        </div>
                        <div className="mt-3">
                          <OrderMetaChips order={o} />
                        </div>
                        <div className="mt-4">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="uppercase tracking-widest text-zinc-500 font-semibold">Progress</span>
                            <span className="font-mono font-semibold">{progress}%</span>
                          </div>
                          <div className="h-2 rounded-full bg-zinc-100 dark:bg-zinc-800 overflow-hidden">
                            <div className="h-full bg-[#C8102E]" style={{ width: `${progress}%` }} />
                          </div>
                        </div>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            )}

            {!loading && completedOrders.length > 0 && (
              <div className="text-sm text-zinc-500">
                <span className="font-semibold">{completedOrders.length}</span> completed orders also visible —{" "}
                <Link to="/orders?status=completed" className="text-[#C8102E] hover:underline font-semibold">view all</Link>
              </div>
            )}
          </div>

          <div className="xl:col-span-1">
            <ActivityFeed orders={orders} limit={12} />
          </div>
        </div>
      </section>
    </div>
  );
}

function AwaitingVendorReceipt({ orders, totalPendingSpecialty }) {
  const previewCount = 4;
  const shown = orders.slice(0, previewCount);
  const remaining = orders.length - shown.length;

  return (
    <section data-testid="awaiting-vendor-tile">
      <Card className="overflow-hidden border-purple-200 dark:border-purple-900/50">
        <div className="bg-gradient-to-r from-purple-50 to-white dark:from-purple-950/30 dark:to-zinc-900 px-5 py-4 border-b border-purple-100 dark:border-purple-900/30 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 flex items-center justify-center shrink-0">
              <Cube size={22} weight="bold" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-purple-700 dark:text-purple-300 font-bold">
                Awaiting vendor receipt
              </div>
              <h2 className="font-heading font-black text-xl mt-0.5" data-testid="awaiting-vendor-heading">
                {orders.length} {orders.length === 1 ? "order" : "orders"} ·{" "}
                <span className="text-purple-700 dark:text-purple-300">
                  {totalPendingSpecialty} {totalPendingSpecialty === 1 ? "item" : "items"}
                </span>{" "}
                pending
              </h2>
            </div>
          </div>
          <Link
            to="/orders?awaiting=specialty"
            className="text-sm font-semibold text-purple-700 dark:text-purple-300 hover:underline inline-flex items-center gap-1"
            data-testid="awaiting-vendor-view-all"
          >
            View all <ArrowRight size={14} weight="bold" />
          </Link>
        </div>

        <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
          {shown.map((o) => {
            const total = o.specialty_count || 0;
            const received = o.specialty_received_count || 0;
            const pending = Math.max(0, total - received);
            return (
              <li key={o.id} data-testid={`awaiting-row-${o.order_number}`}>
                <Link
                  to={`/orders/${o.id}`}
                  className="flex items-center gap-3 px-5 py-3 hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-heading font-black text-base truncate">
                      {o.order_number}
                      <span className="font-normal text-zinc-500 text-sm ml-2">{o.customer_name}</span>
                    </div>
                    <div className="mt-1.5">
                      <OrderMetaChips order={o} />
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-heading font-black text-2xl tabular-nums text-purple-700 dark:text-purple-300">
                      {pending}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
                      pending
                    </div>
                  </div>
                  <ArrowRight size={14} className="text-zinc-300" />
                </Link>
              </li>
            );
          })}
        </ul>

        {remaining > 0 && (
          <div className="px-5 py-3 text-xs text-zinc-500 bg-zinc-50 dark:bg-zinc-800/30 border-t border-zinc-100 dark:border-zinc-800">
            +{remaining} more {remaining === 1 ? "order" : "orders"} pending vendor delivery —{" "}
            <Link
              to="/orders?awaiting=specialty"
              className="text-purple-700 dark:text-purple-300 font-semibold hover:underline"
            >
              see all
            </Link>
          </div>
        )}
      </Card>
    </section>
  );
}
